import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  adminService,
  DailyMissionsAdminBundle,
  UpdateDailyMissionsAdmin,
  UpdateDailyMissionAdminItem,
  UpdateDailyMissionRewardAdmin,
} from '../services/api';

const KIND_ORDER = ['SoloPlays', 'PvpPlays', 'PvPRoomsOpened', 'PerfectQuizzes'] as const;

const INVENTORY_ITEM_KEYS = [
  'SmallHealthPotion',
  'MediumHealthPotion',
  'LargeHealthPotion',
  'SmallExperiencePotion',
  'MediumExperiencePotion',
  'LargeExperiencePotion',
] as const;

function bundleToForm(b: DailyMissionsAdminBundle): UpdateDailyMissionsAdmin {
  const order = (k: string) => {
    const i = KIND_ORDER.indexOf(k as (typeof KIND_ORDER)[number]);
    return i === -1 ? 99 : i;
  };
  const missions = [...b.missions]
    .sort((a, b) => order(a.kind) - order(b.kind))
    .map(
      (m): UpdateDailyMissionAdminItem => ({
        kind: m.kind,
        targetCount: m.targetCount,
        rewards: m.rewards.map(
          (r): UpdateDailyMissionRewardAdmin => ({
            rewardType: r.rewardType,
            xpAmount: r.xpAmount ?? undefined,
            itemTypeKey: r.itemTypeKey ?? undefined,
            itemQuantity: r.itemQuantity > 0 ? r.itemQuantity : 1,
          })
        ),
      })
    );
  return { missions };
}

const AdminDailyMissions: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<UpdateDailyMissionsAdmin | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const b = await adminService.getDailyMissions();
      setForm(bundleToForm(b));
    } catch {
      setError('Failed to load daily missions configuration.');
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateMission = (kind: string, patch: Partial<UpdateDailyMissionAdminItem>) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        missions: prev.missions.map((m) => (m.kind === kind ? { ...m, ...patch } : m)),
      };
    });
  };

  const updateReward = (kind: string, index: number, patch: Partial<UpdateDailyMissionRewardAdmin>) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        missions: prev.missions.map((m) => {
          if (m.kind !== kind) return m;
          const rewards = m.rewards.map((r, i) => (i === index ? { ...r, ...patch } : r));
          return { ...m, rewards };
        }),
      };
    });
  };

  const addReward = (kind: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        missions: prev.missions.map((m) => {
          if (m.kind !== kind) return m;
          const next: UpdateDailyMissionRewardAdmin = {
            rewardType: 'Xp',
            xpAmount: 100,
            itemQuantity: 1,
          };
          return { ...m, rewards: [...m.rewards, next] };
        }),
      };
    });
  };

  const removeReward = (kind: string, index: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        missions: prev.missions.map((m) => {
          if (m.kind !== kind) return m;
          const rewards = m.rewards.filter((_, i) => i !== index);
          return { ...m, rewards };
        }),
      };
    });
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminService.updateDailyMissions(form);
      setForm(bundleToForm(updated));
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' && e !== null && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!form || form.missions.length !== KIND_ORDER.length) {
    return (
      <div className="admin-tab-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Button variant="primary" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      <h3>Daily missions</h3>
      <p className="text-muted">
        Configure the four fixed daily tracks (solo plays, PvP plays, PvP rooms opened as host, perfect quizzes),
        targets, and rewards. Player progress resets at 02:00 UTC.
      </p>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {form.missions.map((m) => (
        <Card key={m.kind} className="mb-3">
          <Card.Header className="fw-semibold">{m.kind}</Card.Header>
          <Card.Body>
            <Form.Group className="mb-3" controlId={`target-${m.kind}`}>
              <Form.Label>Target count</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={m.targetCount}
                onChange={(e) =>
                  updateMission(m.kind, { targetCount: Math.max(1, parseInt(e.target.value, 10) || 1) })
                }
              />
            </Form.Group>

            <div className="fw-semibold mb-2">Rewards</div>
            {m.rewards.map((r, idx) => (
              <Row key={`${m.kind}-r-${idx}`} className="g-2 mb-2 align-items-end">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small text-muted">Type</Form.Label>
                    <Form.Select
                      value={r.rewardType}
                      onChange={(e) => {
                        const t = e.target.value;
                        if (t === 'Xp') {
                          updateReward(m.kind, idx, {
                            rewardType: 'Xp',
                            xpAmount: r.xpAmount ?? 100,
                            itemTypeKey: undefined,
                          });
                        } else {
                          updateReward(m.kind, idx, {
                            rewardType: 'InventoryItem',
                            xpAmount: undefined,
                            itemTypeKey: r.itemTypeKey || 'SmallHealthPotion',
                            itemQuantity: r.itemQuantity ?? 1,
                          });
                        }
                      }}
                    >
                      <option value="Xp">XP</option>
                      <option value="InventoryItem">Inventory item</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {r.rewardType === 'Xp' ? (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">XP amount</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={r.xpAmount ?? ''}
                        onChange={(e) =>
                          updateReward(m.kind, idx, { xpAmount: Math.max(1, parseInt(e.target.value, 10) || 1) })
                        }
                      />
                    </Form.Group>
                  </Col>
                ) : (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small text-muted">Item</Form.Label>
                        <Form.Select
                          value={r.itemTypeKey || 'SmallHealthPotion'}
                          onChange={(e) => updateReward(m.kind, idx, { itemTypeKey: e.target.value })}
                        >
                          {INVENTORY_ITEM_KEYS.map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="small text-muted">Qty</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          value={r.itemQuantity ?? 1}
                          onChange={(e) =>
                            updateReward(m.kind, idx, {
                              itemQuantity: Math.max(1, parseInt(e.target.value, 10) || 1),
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                <Col md="auto" className="ms-auto">
                  <Button variant="outline-danger" size="sm" onClick={() => removeReward(m.kind, idx)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}

            <Button variant="outline-secondary" size="sm" onClick={() => addReward(m.kind)}>
              Add reward
            </Button>
          </Card.Body>
        </Card>
      ))}

      <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
        {saving ? 'Saving…' : 'Save all'}
      </Button>
    </div>
  );
};

export default AdminDailyMissions;
